import { DateTime } from "luxon";

const getTimestamp = (timeString) => {
	let iso;
	if (timeString.startsWith("ago")) {
		const seconds = timeString.substring(timeString.indexOf("(") + 1, timeString.lastIndexOf("s"));
		iso = DateTime.now().minus({ seconds: seconds }).toISO();
	} else {
		iso = timeString.split("'")[1];
	}

	return iso;
};

export const handleSetTickType = (start, end) => {
	const startDateTime = DateTime.fromISO(start);
	const endDateTime = DateTime.fromISO(end);
	const diffInMillis = endDateTime.diff(startDateTime);

	let type = "";

	const { milliseconds } = diffInMillis.values;

	if (milliseconds <= 60 * 1000) {
		//1 minute
		type = "second";
	} else if (milliseconds <= 3 * 60 * 60 * 1000) {
		//3 hours
		type = "minute";
	} else if (milliseconds <= 24 * 60 * 60 * 1000) {
		//1 day
		type = "hour";
	} else if (milliseconds <= 7 * 24 * 60 * 60 * 1000) {
		//1 week
		type = "day";
	} else if (milliseconds <= 31 * 24 * 60 * 60 * 1000) {
		//31 days
		type = "week";
	} else if (milliseconds <= 90 * 24 * 60 * 60 * 1000) {
		//90 days
		type = "month";
	} else if (milliseconds <= 365 * 24 * 60 * 60 * 1000) {
		//365 days
		type = "quarter";
	} else {
		type = "year";
	}

	return type;
};

export const getUnitComparingTimestamps = (data) => {
	let from = data[0].data[0].timestamp;
	let to = data[0].data[data[0].data.length - 1].timestamp;

	if (data.length > 1) {
		data.forEach(({ data }) => {
			if (data[0].timestamp < from) {
				from = data[0].timestamp;
			} else if (data[data.length - 1].timestamp > to) {
				to = data[data.length - 1].timestamp;
			}
		});
	}
	return handleSetTickType(from, to);
};

export const getDatasetValueRange = (datasetsByKey) => {
	const datasets = Object.values(datasetsByKey ?? {});
	if (datasets.length === 0) {
		return null;
	}

	const values = datasets.flatMap((dataset) =>
		dataset
			.map((point) => {
				const raw = point?.measure_value;
				if (raw === null || raw === undefined) {
					return null;
				}

				const value = typeof raw === "number" ? raw : Number(raw);
				return Number.isFinite(value) ? value : null;
			})
			.filter((value) => Number.isFinite(value)),
	);

	if (!values.length) {
		return null;
	}

	const minValue = Math.min(...values);
	const maxValue = Math.max(...values);

	if (minValue === maxValue) {
		const base = Math.abs(minValue) || 1;
		const padding = base * 0.1;
		return {
			min: minValue - padding,
			max: maxValue + padding,
		};
	}

	return {
		min: minValue,
		max: maxValue,
	};
};

//Line colours are generated rather than cycled through a fixed palette. The palette held
//seven entries and repeated, so an eight device report drew two devices in the same colour,
//and several of the seven were close enough to confuse even before they repeated. Rotating
//the hue by the golden angle keeps every neighbouring pair far apart on the colour wheel and
//never runs out. The first line keeps the cyan the charts have always opened with.
const FIRST_LINE_HUE = 194;
const GOLDEN_ANGLE_DEGREES = 137.508;
const LINE_SATURATION = "70%";
const LINE_LIGHTNESS = "58%";
//Alpha used for the lines that are not being hovered. Low enough to push them back, high
//enough to still read as a trend rather than vanish.
const DIMMED_LINE_ALPHA = 0.15;

export const getLineColour = (index) => {
	const hue = Math.round((FIRST_LINE_HUE + index * GOLDEN_ANGLE_DEGREES) % 360);
	const base = `${hue}, ${LINE_SATURATION}, ${LINE_LIGHTNESS}`;

	return {
		borderColour: `hsl(${base})`,
		backgroundColour: `hsl(${base}, 0.1)`,
		dimColour: `hsl(${base}, ${DIMMED_LINE_ALPHA})`,
	};
};
